import { GripVertical } from 'lucide-react'

import { appointmentStatusTone, getAppointmentBlockStyle } from '@/lib/calendar/dayCalendar'
import { serviceAccentForAppointment } from '@/lib/calendar/serviceAccent'
import { formatAppointmentStatus } from '@/lib/appointmentStatusLabels'
import { cn } from '@/lib/utils'

/**
 * @param {string} iso
 */
function formatClock(iso) {
  const date = new Date(iso)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
function primaryServiceLabel(appointment) {
  const first = appointment.services?.[0]
  return first?.service?.name ?? 'Appointment'
}

/**
 * @param {string} status
 */
function isDraggableStatus(status) {
  return !['cancelled', 'no_show', 'completed'].includes(status)
}

/**
 * @param {string} tone
 */
function statusDotClass(tone) {
  switch (tone) {
    case 'active':
      return 'bg-[hsl(var(--primary))]'
    case 'checked':
      return 'bg-amber-500'
    case 'confirmed':
      return 'bg-sky-500'
    case 'pending':
      return 'bg-amber-400'
    case 'completed':
      return 'bg-muted-foreground/50'
    case 'cancelled':
      return 'bg-red-400'
    case 'noshow':
      return 'bg-orange-400'
    default:
      return 'bg-muted-foreground/40'
  }
}

/**
 * @param {{
 *   appointment: import('@/types/api').Appointment
 *   stylistColour?: string | null
 *   dayStartMinutes: number
 *   pxPerMinute: number
 *   lane?: { column: number, totalColumns: number }
 *   onDragStart: (appointment: import('@/types/api').Appointment) => void
 *   onSelect?: (appointment: import('@/types/api').Appointment) => void
 *   rescheduleEnabled?: boolean
 *   rescheduling?: boolean
 *   compact?: boolean
 * }} props
 */
export function CalendarAppointmentBlock({
  appointment,
  stylistColour,
  dayStartMinutes,
  pxPerMinute,
  lane,
  onDragStart,
  onSelect,
  rescheduleEnabled = false,
  rescheduling = false,
  compact = false,
}) {
  const tone = appointmentStatusTone(appointment.status)
  const style = getAppointmentBlockStyle(
    appointment.starts_at,
    appointment.ends_at,
    dayStartMinutes,
    pxPerMinute,
  )
  const accent = serviceAccentForAppointment(appointment)
  const borderAccent = stylistColour || accent.border
  const draggable = isDraggableStatus(appointment.status)
  const isShortBlock = style.height < 48
  const useCompact = compact || isShortBlock || (lane?.totalColumns ?? 1) > 1
  const laneWidth = lane ? 100 / lane.totalColumns : 100
  const laneLeft = lane ? (lane.column / lane.totalColumns) * 100 : 0
  const showStatus =
    !useCompact && !['scheduled', 'confirmed'].includes(appointment.status)

  return (
    <div
      className={cn(
        'calendar-appointment-block group absolute z-10 overflow-hidden rounded-[10px] border border-black/[0.04] text-left',
        'shadow-[0_1px_2px_rgba(40,30,20,0.04)] transition-[box-shadow,transform] duration-150',
        useCompact ? 'px-1.5 py-1' : 'px-2.5 py-1.5',
        tone === 'completed' && 'opacity-70',
        tone === 'cancelled' && 'opacity-55 line-through decoration-red-300/70',
        rescheduling && 'pointer-events-none opacity-55',
        onSelect && 'cursor-pointer hover:-translate-y-px hover:shadow-[0_4px_14px_rgba(40,30,20,0.08)]',
      )}
      style={{
        top: style.top,
        height: style.height,
        left: `calc(${laneLeft}% + 3px)`,
        width: `calc(${laneWidth}% - 6px)`,
        backgroundColor: accent.bg,
        borderLeftWidth: 3,
        borderLeftColor: borderAccent,
      }}
      title={`${appointment.customer?.name ?? 'Guest'} · ${formatClock(appointment.starts_at)}–${formatClock(appointment.ends_at)} · ${formatAppointmentStatus(appointment.status)}`}
    >
      <div className="flex h-full items-start gap-1">
        {rescheduleEnabled && draggable ? (
          <span
            draggable={!rescheduling}
            onDragStart={(event) => {
              event.stopPropagation()
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', String(appointment.id))
              onDragStart(appointment)
            }}
            className="mt-0.5 shrink-0 cursor-grab touch-none opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing"
            aria-label={`Drag to reschedule ${appointment.customer?.name ?? 'booking'}`}
          >
            <GripVertical className="h-3 w-3 text-muted-foreground/60" />
          </span>
        ) : null}

        <button
          type="button"
          className="min-w-0 flex-1 text-left focus-visible:outline-none"
          onClick={() => onSelect?.(appointment)}
        >
          {showStatus ? (
            <p className="mb-0.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className={cn('h-1.5 w-1.5 rounded-full', statusDotClass(tone))} aria-hidden />
              {formatAppointmentStatus(appointment.status)}
            </p>
          ) : null}

          <p
            className={cn(
              'truncate font-semibold leading-tight text-foreground',
              useCompact ? 'text-[11px]' : 'text-[13px]',
            )}
          >
            {appointment.customer?.name ?? 'Guest'}
          </p>

          {!useCompact ? (
            <p className="mt-0.5 truncate text-[11px] leading-snug text-muted-foreground">
              {primaryServiceLabel(appointment)}
            </p>
          ) : null}

          <p
            className={cn(
              'mt-0.5 tabular-nums text-muted-foreground/90',
              useCompact ? 'text-[9px]' : 'text-[11px]',
            )}
          >
            {formatClock(appointment.starts_at)}
            {!useCompact ? ` – ${formatClock(appointment.ends_at)}` : ''}
          </p>
        </button>
      </div>
    </div>
  )
}
